import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { gql, useMutation } from '@apollo/client';
import { axiosUploadMediaToOc } from 'utils/rest-clients';
import { CsvTemplateTypeModel, MediaModel, CsvTemplateModel } from '@utmost.co/enterprise-models';
import { CsvTemplateCreateModelInput } from '@utmost.co/enterprise-models';
import { FormLabel, Icon, Dialog, Input, Button, Typography } from '@beeline.com/bp-design-system';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import DropBox from './DropBox';
import { useQuickStartIntl } from './translations/useQuickStartIntl';
import { useMixpanel } from 'src/integrations/mixpanel/useMixpanel';
import { MixpanelEvents } from 'src/integrations/mixpanel/Events/MixpanelEvents';
const CREATE_CSV_TEMPLATE = gql`
  mutation CreateCsvTemplate($csvTemplate: CsvTemplateCreateModelInput!) {
    createCsvTemplate(csvTemplate: $csvTemplate) {
      name
      status
      uid
      type
    }
  }
`;
interface AddTemplateProps {
  onClose: () => void;
  type: CsvTemplateTypeModel;
}
export const AddTemplate: FC<React.PropsWithChildren<AddTemplateProps>> = ({ onClose, type }) => {
  const { showError, showSuccess } = useSnackbar();
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const [createCsvTemplate, { data, loading }] = useMutation<
    {
      createCsvTemplate: CsvTemplateModel;
    },
    {
      csvTemplate: CsvTemplateCreateModelInput;
    }
  >(CREATE_CSV_TEMPLATE);
  const handleAddFile = (newFile: File) => {
    setFile(newFile);
  };
  const intl = useQuickStartIntl();
  const { track } = useMixpanel();
  const handleUpload = useCallback(async () => {
    if (file) {
      const extension = file.name.split('.').pop();
      const renamedFile = new File([file], `${name}.${extension}`, { type: file.type });
      try {
        const mediaResp = await axiosUploadMediaToOc<MediaModel>('/media/', renamedFile, 'CSV_TEMPLATE', () => {});
        createCsvTemplate({ variables: { csvTemplate: { type, templateMediaUid: mediaResp.data.uid, name } } });
        track(MixpanelEvents.importerEvents.TEMPLATE_UPLOAD, { template: mediaResp.data.filename });
      } catch (e) {
        showError(intl.fileUploadFailed);
      }
    }
  }, [createCsvTemplate, showError, file, intl.fileUploadFailed, name, type, track]);
  useEffect(() => {
    if (data) {
      showSuccess(intl.uploadedTemplate({ name: data.createCsvTemplate.name }));
      onClose();
    }
  }, [data, showSuccess, intl, onClose]);
  return (
    <Dialog open onClose={onClose} title={intl.addTemplate}>
      <div>
        <Typography variant="body1">{intl.chooseTemplateToUpload}</Typography>
        <div style={{ marginBottom: '2rem' }}>
          <FormLabel style={{ marginBottom: '0.5rem', marginTop: '2rem' }} htmlFor="template_name">
            {intl.templateName} <span style={{ color: 'red' }}>*</span>
          </FormLabel>
          <Input
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            id="template_name"
            placeholder={intl.enterName}
          />
        </div>
        <DropBox onAddFile={handleAddFile} selectFileMessage={intl.selectFile} />
        {file && (
          <StyledFile href={URL.createObjectURL(file)} download={file.name}>
            {file.name}{' '}
            <Icon
              name="delete"
              size="small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setFile(undefined);
              }}
            />
          </StyledFile>
        )}
        <StyledDialogControl>
          <Button style={{ marginLeft: 'auto', marginRight: 8 }} onClick={onClose} disabled={loading}>
            {intl.cancel}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={name.length === 0 || file === undefined || loading}
            onClick={() => handleUpload()}
          >
            {intl.add}
          </Button>
        </StyledDialogControl>
      </div>
    </Dialog>
  );
};
export const StyledFile = styled.a`
  padding: 1rem;
  color: #0f66bd;
  border: 1px solid #e9edef;
  border-radius: 5px;
  display: flex;
  align-items: center;
  svg {
    margin-left: auto;
    cursor: pointer;
  }
`;
export const StyledDialogControl = styled.div`
  margin-top: 2rem;
  display: flex;
`;
